import React, { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import {
  Typography,
  Container,
  Box,
  Divider,
} from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer/Footer';
import ContactUs from '../components/ContactUs/ContactUs';
import { Link } from 'react-router-dom';
import PaymentForm from 'components/PaymentForm/PaymentForm';
import investorGuide from '../Files/investor-guide.pdf';

const Disclaimer = () => {
  const { state } = useLocation();
  const userData = state?.signupData || {};
  const footerRef = useRef(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  const handleContactUsClick = () => {
    if (footerRef.current) {
      footerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDownload = (e) => {
    e.preventDefault();
    const link = document.createElement('a');
    link.href = investorGuide;
    link.download = 'investor-guide.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SectionTitle = ({ children }) => (
    <Typography 
      sx={{ 
        fontSize: { xs: "17px", md: "19px" }, 
        fontWeight: "600", 
        color: "#28947F",
        mt: 3,
        mb: 1.5
      }}
    >
      {children}
    </Typography>
  );

  const BodyText = ({ children }) => (
    <Typography 
      sx={{ 
        fontSize: { xs: "14px", md: "15px" },
        lineHeight: 1.8,
        color: "#2c2c2c",
        mb: 2,
        textAlign: "justify"
      }}
    >
      {children}
    </Typography>
  );

  const BulletPoint = ({ children }) => (
    <Box sx={{ display: "flex", gap: 1.5, mb: 2, pl: 2 }}>
      <Box 
        sx={{ 
          minWidth: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#28947F",
          mt: 1.2
        }}
      />
      <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, lineHeight: 1.8, color: "#2c2c2c", textAlign: "justify" }}>
        {children}
      </Typography>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        minHeight: "100vh", 
        display: "flex", 
        flexDirection: "column", 
        background: `linear-gradient(90deg, rgba(209,230,226,1) 0%, rgba(234,238,236,1) 95%)`,
      }}
    >
      <Helmet>
        <title>Operational Disclosure & Terms of Engagement - InvestoFarms</title>
        <meta name="description" content="Important information before you lease with InvestoFarms. Review operational details, risks, and terms." />
      </Helmet>

      <Navbar onContactUsClick={handleContactUsClick} />

      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        <Box sx={{ maxWidth: "1100px", margin: "0 auto", px: { xs: 2, md: 3 } }}>
          {/* Header */}
          <Typography 
            sx={{ 
              fontSize: { xs: "28px", md: "36px" }, 
              fontWeight: "700", 
              color: "#28947F", 
              textAlign: "center",
              mb: 5
            }}
          >
            Operational Disclosure & Terms of Engagement
          </Typography>

          {/* Main Introduction */}
          <Typography 
            sx={{ 
              fontSize: { xs: "17px", md: "19px" }, 
              fontWeight: "600", 
              color: "#28947F",
              mb: 2
            }}
          >
            Important Information Before You Lease
          </Typography>

          <BodyText>
            Thank you for your interest in owning a managed farm with InvestoFarms! Before proceeding with your plot allotment, we kindly ask you to review the following important operational details:
          </BodyText>

          {/* Key Points as Bullet List */}
          <Box sx={{ mb: 3 }}>
            <BulletPoint>
              <strong style={{ color: "#28947F" }}>Non-Ownership of Land:</strong> InvestoFarms provides a service model for managed farmlands. You are entering into a sub-lease agreement for a specific plot; you will not have a permanent legal title to the land. Your participation is tied to the agricultural performance and harvest of your allotted plot.
            </BulletPoint>
            
            <BulletPoint>
              <strong style={{ color: "#28947F" }}>Harvest Ownership & Variable Yield:</strong> Our model allows you to own 100% of the produce. While we aim for optimal yields through professional management, agriculture is subject to nature. There is no guarantee of specific harvest quantities or market prices, and your outcomes are subject to inherent agricultural risks.
            </BulletPoint>
            
            <BulletPoint>
              <strong style={{ color: "#28947F" }}>Risk Factors:</strong> Managed farming carries inherent risks such as weather fluctuations, pest outbreaks, and market price volatility. We recommend you carefully consider your goals, risk tolerance, and financial capacity before committing to a lease.
            </BulletPoint>
            
            <BulletPoint>
              <strong style={{ color: "#28947F" }}>Minimum Entry:</strong> There is a minimum plot size or lease amount required to participate in each project. Please refer to specific project details for more information.
            </BulletPoint>
            
            <BulletPoint>
              <strong style={{ color: "#28947F" }}>Lease Term & Commitment:</strong> Leasing with InvestoFarms typically involves a fixed tenure (lock-in period) to respect the biological crop cycles. Please review your specific project offering for details on the commitment timeframe.
            </BulletPoint>
            
            <BulletPoint>
              <strong style={{ color: "#28947F" }}>Regulatory Compliance:</strong> InvestoFarms strives to comply with all applicable local land and agricultural regulations. Please ensure you are eligible to enter into a sub-lease agreement based on your residency and relevant local laws.
            </BulletPoint>
            
            <BulletPoint>
              <strong style={{ color: "#28947F" }}>Independent Review:</strong> We encourage you to conduct your own independent due diligence regarding the project location and crop types before finalizing your lease.
            </BulletPoint>
          </Box>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Additional Resources Section */}
          <SectionTitle>Additional Resources</SectionTitle>
          <BodyText>
            We recommend reviewing our <Link to="/faq" style={{ color: "#28947F", fontWeight: "600", textDecoration: "none", borderBottom: "1px solid #28947F" }}>Frequently Asked Questions (FAQ)</Link> section for further operational details.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Disclaimer Section */}
          <SectionTitle>Disclaimer</SectionTitle>
          <BodyText>
            Participating in managed farming involves biological and market risks; there is no guarantee of profit. Past harvest performance is not indicative of future results. The information provided on this page is for informational purposes regarding our services and should not be considered financial or investment advice. Please consult with a legal or tax professional regarding agricultural income and leasing.
          </BodyText>

          <Divider sx={{ my: 3, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Acknowledgment Section */}
          <SectionTitle>Acknowledgment</SectionTitle>
          <BodyText>
            By proceeding to the payment gateway, you acknowledge that you have read and understood the service model and the operational terms presented on this page.
          </BodyText>

          <Divider sx={{ my: 4, borderColor: "rgba(40, 148, 127, 0.2)" }} />

          {/* Payment Form Section */}
          <Box sx={{ mt: 4 }}>
            <Typography 
              sx={{ 
                fontSize: { xs: "20px", md: "24px" }, 
                fontWeight: "600", 
                color: "#28947F",
                mb: 3,
                textAlign: "center"
              }}
            >
              Complete Your Investment
            </Typography>
            <Box 
              sx={{ 
                '& input, & select, & textarea': {
                  fontSize: { xs: '14px', md: '15px' }
                },
                '& label': {
                  fontSize: { xs: '14px', md: '15px' }
                },
                '& button': {
                  fontSize: { xs: '15px', md: '16px' }
                },
                '& .MuiTypography-root': {
                  fontSize: { xs: '14px', md: '15px' }
                }
              }}
            >
              <PaymentForm userdata={userData} />
            </Box>
          </Box>
        </Box>
      </Container>

      <Box sx={{ mt: "auto" }}>
        <ContactUs />
        <Footer ref={footerRef} />
      </Box>
    </Box>
  );
};

export default Disclaimer;